import {
  ActionType,
  AppBridge,
  AppBridgeProvider,
  AppContext,
  AppIframeParams,
  DashboardEventFactory,
  EventType,
  actions,
  useAppBridge
} from "../chunk-DNXN5DJT.mjs";
import "../chunk-NHGUNOYT.mjs";
import {
  SALEOR_API_URL_HEADER,
  SALEOR_AUTHORIZATION_BEARER_HEADER,
  SALEOR_DOMAIN_HEADER
} from "../chunk-IEDETZPT.mjs";

// src/app-bridge/fetch.ts
import { useMemo } from "react";
var createAuthenticatedFetch = (appBridge, fetch = global.fetch) => (input, init) => {
  const { token, domain, saleorApiUrl } = appBridge.getState();
  const headers = new Headers(init == null ? void 0 : init.headers);
  headers.set(SALEOR_DOMAIN_HEADER, domain);
  headers.set(SALEOR_AUTHORIZATION_BEARER_HEADER, token ?? "");
  headers.set(SALEOR_API_URL_HEADER, saleorApiUrl ?? "");
  const clonedInit = {
    ...init ?? {},
    headers
  };
  return fetch(input, clonedInit);
};
var useAuthenticatedFetch = (fetch = window.fetch) => {
  const { appBridge } = useAppBridge();
  if (!appBridge) {
    throw new Error("useAuthenticatedFetch can be used only in browser context");
  }
  return useMemo(() => createAuthenticatedFetch(appBridge, fetch), [appBridge, fetch]);
};

// src/app-bridge/use-dashboard-token.ts
import debugPkg from "debug";
import * as jose from "jose";
import { useMemo as useMemo2 } from "react";
var debug = debugPkg.debug("app-sdk:AppBridge:useDashboardToken");
var useDashboardToken = () => {
  const { appBridgeState } = useAppBridge();
  const tokenClaims = useMemo2(() => {
    try {
      if (appBridgeState == null ? void 0 : appBridgeState.token) {
        debug("Trying to decode JWT token from dashboard");
        return jose.decodeJwt(appBridgeState == null ? void 0 : appBridgeState.token);
      }
    } catch (e) {
      debug("Failed decoding JWT token");
      console.error(e);
    }
    return null;
  }, [appBridgeState == null ? void 0 : appBridgeState.token]);
  return {
    isTokenValid: !!tokenClaims,
    tokenClaims,
    hasAppToken: Boolean(appBridgeState == null ? void 0 : appBridgeState.token)
  };
};

// src/app-bridge/with-authorization.tsx
import * as React from "react";

// src/util/is-in-iframe.ts
var isInIframe = () => {
  if (!document || !window) {
    throw new Error("isInIframe should be called only in browser");
  }
  try {
    return document.location !== window.parent.location;
  } catch (e) {
    return false;
  }
};

// src/util/use-is-mounted.ts
import { useEffect, useState } from "react";
var useIsMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
};

// src/app-bridge/with-authorization.tsx
function SimpleError({ children }) {
  return /* @__PURE__ */ React.createElement("div", {
    style: { padding: 32, color: "red" }
  }, /* @__PURE__ */ React.createElement("p", null, children));
}
var defaultProps = {
  dashboardTokenInvalid: /* @__PURE__ */ React.createElement(SimpleError, null, "Dashboard token is invalid"),
  noDashboardToken: /* @__PURE__ */ React.createElement(SimpleError, null, 'Dashboard token doesn"t exist'),
  notIframe: /* @__PURE__ */ React.createElement(SimpleError, null, "The view can only be displayed inside iframe."),
  unmounted: /* @__PURE__ */ React.createElement("p", null, "Loading")
};
var withAuthorization = (props = defaultProps) => (BaseComponent) => {
  const { dashboardTokenInvalid, noDashboardToken, notIframe, unmounted } = {
    ...defaultProps,
    ...props
  };
  function AuthorizedPage(innerProps) {
    const mounted = useIsMounted();
    const { isTokenValid, hasAppToken } = useDashboardToken();
    if (!mounted) {
      return unmounted;
    }
    if (!isInIframe()) {
      return notIframe;
    }
    if (!hasAppToken) {
      return noDashboardToken;
    }
    if (!isTokenValid) {
      return dashboardTokenInvalid;
    }
    return /* @__PURE__ */ React.createElement(BaseComponent, {
      ...innerProps
    });
  }
  return AuthorizedPage;
};

// src/app-bridge/index.ts
var createApp = (targetDomain) => new AppBridge(targetDomain ? { targetDomain } : void 0);
var app_bridge_default = createApp;
export {
  ActionType,
  AppBridge,
  AppBridgeProvider,
  AppContext,
  AppIframeParams,
  DashboardEventFactory,
  EventType,
  actions,
  createApp,
  createAuthenticatedFetch,
  app_bridge_default as default,
  useAppBridge,
  useAuthenticatedFetch,
  useDashboardToken,
  withAuthorization
};
